import { useState, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { Box, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useBufferZones } from "src/api/bufferZone";
import { SelectableObject } from "src/components/Form/types";
import courseCategoryService, { CourseCategory } from "src/services/courseCategory";
import { captureHandledException } from "src/services/sentry";

import { BasePaper, Row, StyledSwitch } from "../../components/Base";
import Button from "../../components/Button";
import { Form, Input, InputForm, Select, Switch, Textarea } from "../../components/Form";
import DynamicList from "../../components/DynamicList";
import { getValidPeriods, ValidPeriod } from "../../api/validPeriods";
import {
  addMoodleCourse,
  getMoodleCourseById,
  getMoodleCourses,
  updateMoodleCourse,
  createCompanyCourse,
  getCompanyCourseById,
  getCompanyCoursesForCrew,
  updateCompanyCourse,
  CourseRequest,
  CompanyCourseRequest,
  CourseResponse,
  RefresherCourseResponse,
  getRefresherSeasons,
  Season,
} from "../../api/courses";
import { getAuthorizedUsers, AuthorizedUser } from "../../api/crew";
import { BUFFER_ZONES } from "../../constants";
import RefresherRow, { RefreshCourseRowModel } from "./refresherRow";
import { QuotaExceededError } from "../../api/request";

const createNewCourse = (): CourseResponse => ({
  id: 0,
  name: "",
  shortName: "",
  displayName: "",
  type: "",
  startDate: 0,
  summary: "",
  summary2: "",
  visible: 1,
  showAtAllCourses: 0,
  isConfigured: false,
  buffer: BUFFER_ZONES[2],
  price: "",
  refreshers: [],
  validPeriod: undefined,
  validPeriodId: undefined,
  courseCategory: undefined,
  courseCategoryId: undefined,
});

interface CourseForm {
  name: string;
  shortName: string;
  displayName: string;
  summary: string;
  summary2?: string;
  showAtAllCourses: boolean;
  isRefresher: boolean;
}

const mapCourseForm = (course: CourseResponse): CourseForm => ({
  name: course.name,
  shortName: course.shortName,
  displayName: course.displayName,
  summary: course.summary || "",
  summary2: course.summary2 || "",
  showAtAllCourses: !!course.showAtAllCourses,
  isRefresher: course.isRefresher ?? false,
});

const MoodleCourseEdit = () => {
  const history = useHistory();
  const goBack = () => history.goBack();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { courseId: courseIdParam, crewId } = useParams<{ courseId?: string; crewId?: string }>();
  const courseId = courseIdParam ? Number(courseIdParam) : null;
  const isCompanyCourse = !!crewId;

  if (courseIdParam && (isNaN(courseId!) || courseId! <= 0)) {
    throw new Error(t("invalid_course_id", { courseId: courseIdParam }));
  }

  const crewIdNum = crewId ? Number(crewId) : null;

  // Form state
  const [course, setCourse] = useState<CourseForm | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedRefreshers, setSelectedRefreshers] = useState<RefreshCourseRowModel[]>([]);
  // Price is handled out of the form because of Free switch
  const [coursePrice, setCoursePrice] = useState(0);
  const [isCoursePriceFree, setIsCoursePriceFree] = useState<boolean>(false);
  const [isPriceFocused, setIsPriceFocused] = useState<boolean>(false);

  // Default validity period is handled out of the form because Buffer zone depends on it
  const [defaultValidPeriod, setDefaultValidPeriod] = useState<SelectableObject | "">("");
  const [defaultCourseCategory, setDefaultCourseCategory] = useState<SelectableObject | "">("");
  const [bufferZone, setBufferZone] = useState<SelectableObject | "">("");

  // Select and autocomplete options
  const bufferZones = useBufferZones();
  const [allValidPeriods, setAllValidPeriods] = useState<ValidPeriod[]>([]);
  const [allCourseCategories, setAllCourseCategories] = useState<CourseCategory[]>([]);
  const [allRefreshers, setAllRefreshers] = useState<RefreshCourseRowModel[]>([]);
  const [allAuthorizedUsers, setAllAuthorizedUsers] = useState<AuthorizedUser[]>([]);
  const [selectedAuthorizedSignatory, setSelectedAuthorizedSignatory] = useState<SelectableObject | "">("");

  function toggleCoursePriceFree(isCoursePriceFree: boolean) {
    setIsCoursePriceFree(isCoursePriceFree);
    if (isCoursePriceFree) {
      setCoursePrice(0);
    }
  }

  const isValidPeriodExpire = useMemo(() => {
    return !!defaultValidPeriod && +defaultValidPeriod.getId() > 1;
  }, [defaultValidPeriod]);

  useEffect(() => {
    (async () => {
      const [moodleCourse, allValidPeriods, allRefreshers, allCourseCategories, authorizedUsers] = await Promise.all([
        courseId
          ? isCompanyCourse
            ? getCompanyCourseById(crewIdNum!, String(courseId))
            : getMoodleCourseById(String(courseId))
          : Promise.resolve(createNewCourse()),
        getValidPeriods(),
        isCompanyCourse ? getCompanyCoursesForCrew(crewIdNum!) : getMoodleCourses(),
        Promise.resolve(courseCategoryService.getOptions()),
        isCompanyCourse ? getAuthorizedUsers(crewId!) : Promise.resolve([]),
      ]);

      if (moodleCourse.isPartOfTraining) {
        const seasons = await getRefresherSeasons();
        setSeasons(seasons);
      }

      setAllValidPeriods(allValidPeriods);
      setAllCourseCategories(allCourseCategories);
      setAllRefreshers(
        allRefreshers
          .filter((r: CourseResponse) => !isCompanyCourse || !!r.isRefresher)
          .map((r: CourseResponse) => new RefreshCourseRowModel(r)),
      );
      setAllAuthorizedUsers(authorizedUsers);

      setCoursePrice(Number(moodleCourse.price ?? 0));
      setIsCoursePriceFree(isCompanyCourse || moodleCourse.price === null || Number(moodleCourse.price) === 0);

      const validPeriod = moodleCourse.validPeriodId
        ? allValidPeriods.find((vp: ValidPeriod) => vp.isEqual(moodleCourse.validPeriodId!))
        : undefined;
      setDefaultValidPeriod(validPeriod || "");

      const courseCategory = moodleCourse.courseCategoryId
        ? allCourseCategories.find((t: CourseCategory) => t.isEqual(moodleCourse.courseCategoryId!))
        : undefined;
      setDefaultCourseCategory(courseCategory || "");

      const bufferZone = bufferZones.find((bz) => bz.isEqual(moodleCourse.buffer ?? BUFFER_ZONES[2]))!;
      setBufferZone(bufferZone);

      setSelectedRefreshers(
        moodleCourse.refreshers?.map((r: RefresherCourseResponse) => new RefreshCourseRowModel(r)) || [],
      );

      if (isCompanyCourse) {
        const authorizedSignatory = authorizedUsers.find(
          (u: AuthorizedUser) => u.id === (moodleCourse as any).authorizedSignatory?.userId,
        );
        if (authorizedSignatory) {
          setSelectedAuthorizedSignatory(authorizedSignatory);
        } else if (!courseId && authorizedUsers.length === 1) {
          setSelectedAuthorizedSignatory(authorizedUsers[0]);
        } else {
          setSelectedAuthorizedSignatory("");
        }
      }

      setCourse(mapCourseForm(moodleCourse));
    })();
  }, [courseId, crewId, isCompanyCourse]);

  const onSubmit = async (courseForm: CourseForm) => {
    if (!defaultValidPeriod) {
      enqueueSnackbar(t("course.validity_period_required"), { variant: "error" });
      return;
    }

    if (isCompanyCourse) {
      if (!defaultCourseCategory) {
        enqueueSnackbar(t("course.category_required"), { variant: "error" });
        return;
      }
      if (isValidPeriodExpire && !bufferZone) {
        enqueueSnackbar(t("course.buffer_zone_required"), { variant: "error" });
        return;
      }
      if (!selectedAuthorizedSignatory) {
        enqueueSnackbar(t("course.authorized_signatory_required"), { variant: "error" });
        return;
      }

      // Company course logic
      const courseRequest = {
        ...courseForm,
        summary: courseForm.summary || null,
        summary2: courseForm.summary2 || null,
        shortName: courseForm.shortName || courseForm.name,
        displayName: courseForm.displayName || courseForm.name,
        type: "company",
        startDate: Date.now(),
        visible: 1,
        isConfigured: true,
        validPeriodId: (defaultValidPeriod && defaultValidPeriod.getId()) || null,
        courseCategoryId: (defaultCourseCategory && defaultCourseCategory.getId()) || null,
        bufferZone: isValidPeriodExpire ? bufferZone && bufferZone.getId() : null,
        price: 0, // Company courses are always free
        showAtAllCourses: courseForm.showAtAllCourses ? 1 : 0,
        isRefresher: courseForm.isRefresher,
        authorizedSignatoryId: (selectedAuthorizedSignatory && selectedAuthorizedSignatory.getId()) || null,
        refreshers: !courseForm.isRefresher
          ? selectedRefreshers.map((refresher) => ({
              id: refresher.id,
              isActive: refresher.isActive ? 1 : 0,
              season: null,
            }))
          : [],
      };

      const handleCompanyCourseError = async (error: unknown): Promise<{ shortName: string } | undefined> => {
        captureHandledException(error, {
          feature: "moodleCourse",
          action: courseId ? "updateCompanyCourse" : "createCompanyCourse",
          crewId: crewIdNum,
          courseId,
        });

        if (error instanceof Response) {
          switch (error.status) {
            case 400:
              enqueueSnackbar(t("course.save_failed"), { variant: "error" });
              break;
            case 404:
              enqueueSnackbar(t("validated_action_button.not_found"), { variant: "error" });
              break;
            case 409:
              return { shortName: t("course.duplicate_course_name") };
            case 500:
            case 503:
              enqueueSnackbar(t("validated_action_button.server_error_retry"), { variant: "error" });
              break;
            default:
              enqueueSnackbar(t("course.save_failed"), { variant: "error" });
          }
        } else if (error instanceof QuotaExceededError) {
          enqueueSnackbar(t("course.quota_exceeded"), { variant: "warning" });
        } else if (error instanceof TypeError) {
          enqueueSnackbar(t("validated_action_button.network_error"), { variant: "error" });
        } else {
          enqueueSnackbar(t("validated_action_button.unknown_error"), { variant: "error" });
        }
      };

      if (courseId) {
        try {
          await updateCompanyCourse(crewIdNum!, String(courseId), courseRequest as CompanyCourseRequest);
          history.push(`/crew/${crewId}/companyCourses`);
        } catch (error) {
          return handleCompanyCourseError(error);
        }
      } else {
        try {
          await createCompanyCourse(crewIdNum!, courseRequest as CompanyCourseRequest);
          history.push(`/crew/${crewId}/companyCourses`);
        } catch (error) {
          return handleCompanyCourseError(error);
        }
      }
    } else {
      // Moodle course logic
      const courseRequest = {
        ...courseForm,
        summary: courseForm.summary || null,
        summary2: courseForm.summary2 || null,
        shortName: courseForm.shortName || courseForm.name,
        displayName: courseForm.displayName || courseForm.name,
        validPeriodId: (defaultValidPeriod && defaultValidPeriod.getId()) || null,
        courseCategoryId: (defaultCourseCategory && defaultCourseCategory.getId()) || null,
        bufferZone: isValidPeriodExpire ? bufferZone && bufferZone.getId() : null,
        price: coursePrice,
        showAtAllCourses: courseForm.showAtAllCourses ? 1 : 0,
        refreshers: selectedRefreshers.map((refresher) => ({
          id: refresher.id,
          isActive: refresher.isActive ? 1 : 0,
          season: refresher.season ?? null,
        })),
      };

      if (courseId) {
        try {
          const result = await updateMoodleCourse(courseId, courseRequest as CourseRequest);
          if (result.ok) {
            history.push("/courses/moodle");
          }
        } catch (error) {
          if (error instanceof Response && error.status === 409) {
            return { shortName: t("course.duplicate_course_name") };
          }
          captureHandledException(error, { feature: "moodleCourse", action: "updateMoodleCourse", courseId });
          enqueueSnackbar(t("course.save_failed"), { variant: "error" });
        }
      } else {
        try {
          const result = await addMoodleCourse(courseRequest as CourseRequest);
          if (result.ok) {
            goBack();
          }
        } catch (error) {
          if (error instanceof Response && error.status === 400) {
            const errorData = await error.json();
            return { shortName: errorData.message };
          }
        }
      }
    }
  };

  return (
    <>
      <Typography component="h2" variant="h4">
        <Button icon={<ArrowBackIcon />} onClick={goBack} />
        {isCompanyCourse
          ? courseId
            ? t("course.edit_company_lms_action")
            : t("course.add_company_lms_action")
          : courseId
            ? t("course.edit_moodle_action")
            : t("course.add_moodle_action")}
      </Typography>
      {course && (
        <BasePaper>
          <Form
            defaultValues={{ ...course }}
            onSubmit={onSubmit}
            validation={{
              name: (value: string) => (!value ? t("course.full_name_required") : ""),
            }}
          >
            {courseId && (
              <Row>
                <Input label={t("course_id")} value={String(courseId)} disabled />
              </Row>
            )}
            <Row>
              <InputForm name="name" label={`${t("course.full_name")}*`} />
            </Row>
            <Row>
              <InputForm name="shortName" label={`${t("course.short_name")}`} />
            </Row>
            <Row>
              <InputForm name="displayName" label={`${t("course.display_name")}`} />
            </Row>
            {!isCompanyCourse && (
              <Row>
                <Box
                  sx={(courseCategory) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: courseCategory.spacing(2),
                  })}
                >
                  <TextField
                    sx={{ width: "100%" }}
                    variant="outlined"
                    size="small"
                    label={t("course.price")}
                    value={isPriceFocused && coursePrice === 0 ? "" : coursePrice}
                    type="number"
                    slotProps={{ htmlInput: { min: 0 } }}
                    onChange={(event) => {
                      const value = event.target.value;
                      const numValue = value === "" ? 0 : +value;
                      setCoursePrice(numValue < 0 ? 0 : numValue);
                    }}
                    onFocus={() => {
                      setIsPriceFocused(true);
                    }}
                    onBlur={() => {
                      setIsPriceFocused(false);
                    }}
                    disabled={isCoursePriceFree}
                  />
                  <Typography component="span">{`${t("free")}:`}</Typography>
                  <StyledSwitch
                    title={t("course.price_free_toggle")}
                    checked={isCoursePriceFree}
                    onChange={() => toggleCoursePriceFree(!isCoursePriceFree)}
                  />
                </Box>
              </Row>
            )}
            <Row>
              <Select
                label={`${t("course.select_validity_period")}*`}
                value={defaultValidPeriod}
                items={allValidPeriods}
                onChange={(selected) => setDefaultValidPeriod(selected)}
                required
              />
            </Row>
            {isValidPeriodExpire && (
              <Row>
                <Select
                  label={`${t("course.select_buffer_zone")}${isCompanyCourse ? "*" : ""}`}
                  value={bufferZone!}
                  items={bufferZones}
                  onChange={(selected) => setBufferZone(selected!)}
                  required={isCompanyCourse}
                />
              </Row>
            )}
            <Row>
              <Select
                label={`${t("course.select_theme")}${isCompanyCourse ? "*" : ""}`}
                value={defaultCourseCategory}
                items={allCourseCategories}
                onChange={(selected) => setDefaultCourseCategory(selected)}
                required={isCompanyCourse}
              />
            </Row>
            {isCompanyCourse && (
              <Row>
                <Select
                  label={`${t("course.authorized_signatory")}*`}
                  value={selectedAuthorizedSignatory}
                  items={allAuthorizedUsers}
                  onChange={(selected) => setSelectedAuthorizedSignatory(selected)}
                  required
                />
              </Row>
            )}
            {!isCompanyCourse && (
              <Row>
                <Typography
                  component="span"
                  sx={{ paddingLeft: "1rem" }}
                >{`${t("course.show_all_courses")}:`}</Typography>
                <Switch name="showAtAllCourses" />
              </Row>
            )}
            <Row>
              <Textarea name="summary2" label={t("certificate_summary")} />
            </Row>
            <Row>
              <Textarea name="summary" label={t("summary")} />
            </Row>
            {isCompanyCourse && (
              <Row>
                <Typography component="span" sx={{ paddingLeft: "1rem" }}>
                  {`${t("course.is_refresher")}:`}
                </Typography>
                <Switch name="isRefresher" />
              </Row>
            )}
            <DynamicList<RefreshCourseRowModel>
              renderChild={(props) => (
                <RefresherRow
                  {...props}
                  allOptions={allRefreshers}
                  excludeOptions={selectedRefreshers}
                  seasons={seasons}
                />
              )}
              initialList={selectedRefreshers}
              onChange={(refreshers) => setSelectedRefreshers(refreshers)}
              addButtonText={t("course.add_refresher_action")}
            />
            <Row>
              <Button type="submit">{courseId ? t("update") : t("create")}</Button>
              <Button onClick={goBack}>{t("cancel")}</Button>
            </Row>
          </Form>
        </BasePaper>
      )}
    </>
  );
};

export default MoodleCourseEdit;
